/**
 * Injects matching styles into the Shopify Forms shadow DOM.
 * Shopify Forms uses a custom element with shadow DOM, so external CSS
 * cannot style the form contents. This script uses a MutationObserver
 * to detect when the shadow DOM is created and injects a <style> tag.
 */
(function () {
  'use strict';

  // CSS to inject — matches party-booking-form styling
  var INJECTED_CSS = `
    /* ===== Host element — stretch to fill parent ===== */
    :host {
      display: block !important;
      width: 100% !important;
      max-width: 100% !important;
      overflow: visible !important;
    }

    /* ===== All containers — ensure full width, no clipping ===== */
    div, section, article {
      max-width: 100% !important;
      box-sizing: border-box !important;
      overflow: visible !important;
    }

    /* ===== Form layout — let the app's own grid handle placement ===== */
    form {
      max-width: 100% !important;
      width: 100% !important;
    }

    /* ===== Inputs ===== */
    input[type="text"],
    input[type="email"],
    input[type="tel"],
    input[type="number"],
    input[type="date"],
    input[type="time"],
    input[type="url"],
    textarea,
    select {
      width: 100% !important;
      height: 48px !important;
      padding: 16px 20px !important;
      font-family: 'Switzer', sans-serif !important;
      font-size: 1rem !important;
      color: #000000 !important;
      background-color: #ffffff !important;
      border: 2px solid transparent !important;
      border-radius: 4px !important;
      outline: none !important;
      transition: border-color 0.2s ease, box-shadow 0.2s ease !important;
      box-sizing: border-box !important;
    }

    textarea {
      height: 160px !important;
      resize: none !important;
      min-height: 160px !important;
    }
    /* Remove ::before that adds white space in textarea/field */
    textarea::before,
    .field:has(textarea)::before {
      display: none !important;
      content: none !important;
    }

    /* Placeholder — match party-booking-form grey (explicit to override app styles) */
    input::placeholder,
    textarea::placeholder,
    input::-webkit-input-placeholder,
    textarea::-webkit-input-placeholder,
    input::-moz-placeholder,
    textarea::-moz-placeholder,
    input:-ms-input-placeholder,
    textarea:-ms-input-placeholder {
      color: rgba(0, 0, 0, 0.5) !important;
      opacity: 1 !important;
    }

    /* Focus / hover */
    input:hover,
    input:focus,
    input:focus-visible,
    textarea:hover,
    textarea:focus,
    textarea:focus-visible,
    select:hover,
    select:focus {
      background-color: #ffffff !important;
      border-color: #e10679 !important;
      box-shadow: 0 0 0 3px rgba(225, 6, 121, 0.2) !important;
    }

    /* ===== Labels ===== */
    .field {
      position: relative !important;
    }
    .field label {
      color: rgba(0, 0, 0, 0.5) !important;
      font-family: 'Switzer', sans-serif !important;
      font-size: 0.9375rem !important;
      transition: none !important;
      transform: none !important;
      pointer-events: none !important;
    }

     /* Textarea: keep label at top of field, not centered */
    .field:has(textarea):not(.field-has-value) label {
      top: 20px;
      left: 24px !important;
      margin-top: 0 !important;
    }
    /* Position label to the right of caret (like a true placeholder) */
    .field:not(.field-has-value):not(.field-is-date):not(has:(textarea)) label {
      position: absolute !important;
      left: 24px !important;
      top: 50% !important;
      margin: 0 !important;
      margin-top: -0.55em !important;
      display: block !important;
    }
    .field-has-value label,
    .field-is-date label {
      position: static !important;
    }

    /* ===== Submit button ===== */
    button[type="submit"],
    input[type="submit"] {
      width: 220px !important;
      height: 58px !important;
      padding: 0 !important;
      font-family: 'ClashDisplay', sans-serif !important;
      font-size: 1.2rem !important;
      font-weight: 700 !important;
      text-transform: uppercase !important;
      color: #000000 !important;
      background-color: #e10679 !important;
      border: none !important;
      border-radius: 50px !important;
      cursor: pointer !important;
      transition: background-color 0.2s ease, transform 0.2s ease !important;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      box-sizing: border-box !important;
      margin: 16px auto 0 !important;
    }

    button[type="submit"]:hover,
    input[type="submit"]:hover {
      background-color: #c4056a !important;
      transform: translateY(-2px) !important;
    }

    /* ===== Headings ===== */
    h1, h2, h3,
    h1[class], h2[class], h3[class] {
      font-family: 'ClashDisplay', sans-serif !important;
      font-size: 32px !important;
      font-weight: 600 !important;
      line-height: 1 !important;
      margin: 0 0 16px 0 !important;
      color: #e10679 !important;
      text-align: center !important;
      text-transform: uppercase !important;
    }

    @media screen and (min-width: 750px) {
      h1, h2, h3,
      h1[class], h2[class], h3[class] {
        font-size: 40px !important;
      }
    }

    /* ===== Hide label when user has typed (placeholder-like behavior) ===== */
    .field-has-value label {
      visibility: hidden !important;
      height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      overflow: hidden !important;
    }

    /* ===== Always hide label for date input ===== */
    div[data-testid="date-birthday"] > div:first-child {
      visibility: hidden !important;
      height: 0 !important;
      margin: 0 !important;
      padding: 0 !important;
      overflow: hidden !important;
    }

    /* ===== Description / subtext ===== */
    p, p[class] {
      font-family: 'Switzer', sans-serif !important;
      font-size: 0.9375rem !important;
      line-height: 1.6 !important;
      margin: 0 0 32px 0 !important;
      color: rgba(255, 255, 255, 0.7) !important;
      text-align: center !important;
    }

    /* ===== Style Shopify Forms validation (simple red text); errors appear BELOW input ===== */
    form output,
    form output[role="alert"],
    form [role="alert"] {
      display: block !important;
      font-family: 'Switzer', sans-serif !important;
      font-size: 0.8125rem !important;
      color: #c00 !important;
      margin-top: 4px !important;
      margin-bottom: 0 !important;
      padding: 0 !important;
      line-height: 1.3 !important;
    }
    form [class*="error"]:not(button):not(a),
    form [class*="Error"]:not(button):not(a),
    form [class*="validation"]:not(button):not(a) {
      font-family: 'Switzer', sans-serif !important;
      font-size: 0.8125rem !important;
      color: #c00 !important;
      margin-top: 4px !important;
      margin-bottom: 0 !important;
      padding: 0 !important;
      line-height: 1.3 !important;
    }
    /* Red border on input whose preceding sibling is an error */
    output + div input,
    output + div textarea,
    output[role="alert"] + div input,
    output[role="alert"] + div textarea {
      border-color: #c00 !important;
      box-shadow: 0 0 0 1px #c00 !important;
    }
    /* No grid placement overrides — the app's own layout handles field order */

    /* ===== Future date validation error ===== */
    .future-date-error {
      color: #c00 !important;
      font-family: 'Switzer', sans-serif !important;
      font-size: 0.8125rem !important;
      margin-top: 6px !important;
      line-height: 1.3 !important;
    }

    /* ===== Success message ===== */
    [class*="success"] {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      gap: 8px !important;
      padding: 12px 16px !important;
      border-radius: 4px !important;
      font-size: 0.875rem !important;
      background-color: rgba(0, 255, 0, 0.1) !important;
      color: #4ade80 !important;
      border: 1px solid rgba(0, 255, 0, 0.3) !important;
    }

    /* ===== Mobile ===== */
    @media screen and (max-width: 749px) {
      button[type="submit"],
      input[type="submit"] {
        width: 100% !important;
        max-width: 280px !important;
      }

      h1, h2, h3 {
        font-size: 28px !important;
        margin-bottom: 12px !important;
      }

      p {
        margin-bottom: 24px !important;
      }

      input[type="text"],
      input[type="email"],
      input[type="tel"],
      input[type="number"],
      input[type="date"],
      input[type="time"],
      input[type="url"] {
        padding: 14px 16px !important;
      }
    }
  `;

  var STYLE_ID = 'shopify-forms-party-match';

  function injectStyles(shadowRoot) {
    // Don't inject twice
    if (shadowRoot.querySelector('#' + STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = INJECTED_CSS;
    shadowRoot.appendChild(style);

    attachLabelHideBehavior(shadowRoot);
    setTimeout(function () { attachLabelHideBehavior(shadowRoot); }, 500);
    addFutureDateValidation(shadowRoot);
    setTimeout(function () { addFutureDateValidation(shadowRoot); }, 500);
    setTimeout(function () { addFutureDateValidation(shadowRoot); }, 2000);
  }

  function addFutureDateValidation(root) {
    var form = root.querySelector('form');
    if (!form || form.dataset.futureDateAttached) return;

    var yearInput = null, monthInput = null, dayInput = null;
    var dateInput = form.querySelector('input[type="date"]');

    if (dateInput) {
      var today = new Date();
      dateInput.setAttribute('min', today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0') + '-' + String(today.getDate()).padStart(2, '0'));
    } else {
      var allInputs = form.querySelectorAll('input:not([type="hidden"]):not([type="submit"])');
      allInputs.forEach(function (input) {
        var ph = (input.placeholder || '').toUpperCase().trim();
        var name = (input.name || '').toLowerCase();
        if (ph === 'YYYY' || name.indexOf('year') !== -1) yearInput = input;
        if (ph === 'MM' || name.indexOf('month') !== -1) monthInput = input;
        if (ph === 'DD' || name.indexOf('day') !== -1) dayInput = input;
      });
      if (!yearInput || !monthInput || !dayInput) return;
    }

    form.dataset.futureDateAttached = '1';
    var dateContainer = (yearInput || dateInput).closest('div');
    while (dateContainer && dateContainer.parentElement !== form) {
      dateContainer = dateContainer.parentElement;
    }
    if (!dateContainer && dateInput) dateContainer = dateInput.closest('div');

    var errorEl = null;

    function showError(msg) {
      if (!errorEl) {
        errorEl = document.createElement('div');
        errorEl.className = 'future-date-error';
      }
      errorEl.textContent = msg;
      if (dateContainer && !dateContainer.contains(errorEl)) dateContainer.appendChild(errorEl);
    }

    function clearError() {
      if (errorEl && errorEl.parentNode) errorEl.remove();
    }

    function checkDate() {
      if (dateInput) {
        var val = (dateInput.value || '').trim();
        if (!val) return { valid: true };
        var chosen = new Date(val);
        if (isNaN(chosen.getTime())) return { valid: false, msg: 'Please enter a valid date.' };
        var today = new Date();
        today.setHours(0, 0, 0, 0);
        chosen.setHours(0, 0, 0, 0);
        return chosen <= today ? { valid: false, msg: 'Please choose a future date.' } : { valid: true };
      }
      var y = parseInt(yearInput.value, 10);
      var m = parseInt(monthInput.value, 10);
      var d = parseInt(dayInput.value, 10);
      if (!y || !m || !d) return { valid: true };
      if (m < 1 || m > 12 || d < 1 || d > 31) return { valid: false, msg: 'Please enter a valid date.' };
      var chosen = new Date(y, m - 1, d);
      if (chosen.getFullYear() !== y || chosen.getMonth() !== m - 1 || chosen.getDate() !== d) {
        return { valid: false, msg: 'Please enter a valid date.' };
      }
      var today = new Date();
      today.setHours(0, 0, 0, 0);
      chosen.setHours(0, 0, 0, 0);
      return chosen <= today ? { valid: false, msg: 'Please choose a future date.' } : { valid: true };
    }

    if (yearInput && monthInput && dayInput) {
      [yearInput, monthInput, dayInput].forEach(function (input) {
        input.addEventListener('input', clearError);
        input.addEventListener('change', clearError);
      });
    } else if (dateInput) {
      dateInput.addEventListener('input', clearError);
      dateInput.addEventListener('change', clearError);
    }

    root.addEventListener('click', function (e) {
      var btn = e.target.closest('button[type="submit"], input[type="submit"]');
      if (!btn || !form.contains(btn)) return;
      var result = checkDate();
      if (!result.valid) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        showError(result.msg);
      } else {
        clearError();
      }
    }, true);
  }

  function attachLabelHideBehavior(root) {
    var inputs = root.querySelectorAll('input:not([type="hidden"]):not([type="submit"]):not([type="checkbox"]):not([type="radio"]), textarea');
    inputs.forEach(function (input) {
      if (input.dataset.labelHideAttached) return;
      input.dataset.labelHideAttached = '1';

      var field = input.parentElement;
      while (field && !field.querySelector('label')) {
        field = field.parentElement;
      }
      if (!field) return;

      field.classList.add('field');
      if (input.type === 'date') {
        field.classList.add('field-is-date');
      }

      function update() {
        var hasValue = (input.value || '').trim().length > 0;
        if (hasValue) {
          field.classList.add('field-has-value');
        } else {
          field.classList.remove('field-has-value');
        }
      }

      input.addEventListener('input', update);
      input.addEventListener('change', update);
      input.addEventListener('blur', update);
      update();
    });
  }

  function init() {
    var formsRoots = document.querySelectorAll('[data-forms-id], shopify-forms-embed');

    formsRoots.forEach(function (root) {
      // Check all child elements for shadow roots
      function checkChildren(parent) {
        var children = parent.querySelectorAll('*');
        children.forEach(function (child) {
          if (child.shadowRoot) {
            injectStyles(child.shadowRoot);
          }
        });
      }

      // If root itself has shadow
      if (root.shadowRoot) {
        injectStyles(root.shadowRoot);
      }

      // Check existing children
      checkChildren(root);

      // Observe for new elements with shadow DOM
      var observer = new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          mutation.addedNodes.forEach(function (node) {
            if (node.nodeType !== 1) return; // element nodes only

            // Check the added node
            if (node.shadowRoot) {
              injectStyles(node.shadowRoot);
            }

            // Check descendants
            if (node.querySelectorAll) {
              node.querySelectorAll('*').forEach(function (desc) {
                if (desc.shadowRoot) {
                  injectStyles(desc.shadowRoot);
                }
              });
            }
          });
        });

        // Also re-check all children in case shadow roots were added
        checkChildren(root);
      });

      observer.observe(root, { childList: true, subtree: true });
    });
  }

  // Run on DOMContentLoaded or immediately if already loaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Also retry after a delay (Shopify Forms loader is deferred)
  setTimeout(init, 2000);
  setTimeout(init, 5000);
})();
